import { ComponentProps, MouseEvent, useCallback, useState } from 'react';

import { Close } from '@mui/icons-material';
import { Box, Button, IconButton, Popover, Typography } from '@mui/material';
import { Image } from '@scorenco/components';
import { RouteLink, useTranslation } from '@scorenco/core';
import { T_KEYS } from 'translations';

type Sponsor = {
  id?: number;
  logo?: string;
  url?: string;
  name?: string;
  description?: string;
};

type SponsorItemProps = {
  sponsor: Sponsor;
  imageSx: ComponentProps<typeof Image>['sx'];
  loading?: 'eager' | 'lazy';
};

type SponsorDetailContentProps = {
  sponsor: Sponsor;
  onClose: () => void;
};

const SponsorDetailContent = ({
  sponsor,
  onClose,
}: SponsorDetailContentProps) => {
  const { t } = useTranslation();

  return (
    <Box sx={{ maxWidth: 280 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 1,
          mb: 1,
        }}
      >
        <Typography component="h2" variant="subtitle1" fontWeight={700}>
          {sponsor.name ?? t(T_KEYS.SPONSOR_DEFAULT_TITLE)}
        </Typography>
        <IconButton
          size="small"
          onClick={onClose}
          aria-label="Fermer"
          sx={{ mt: -0.5, mr: -0.5 }}
        >
          <Close fontSize="small" />
        </IconButton>
      </Box>

      <Typography
        variant="body2"
        sx={{ whiteSpace: 'pre-wrap', mb: sponsor.url ? 2 : 0 }}
      >
        {sponsor.description}
      </Typography>

      {sponsor.url && (
        <Button
          component="a"
          href={sponsor.url}
          target="_blank"
          rel="noopener noreferrer"
          variant="contained"
          size="small"
          fullWidth
          onClick={onClose}
        >
          {t(T_KEYS.SPONSOR_VISIT_WEBSITE)}
        </Button>
      )}
    </Box>
  );
};

export const SponsorItem = ({
  sponsor,
  imageSx,
  loading,
}: SponsorItemProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const open = Boolean(anchorEl);

  const handleOpen = useCallback((event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const image = (
    <Image
      width={120}
      height={120}
      src={sponsor.logo}
      alt={sponsor.name}
      loading={loading}
      sx={imageSx}
    />
  );

  if (sponsor.description) {
    return (
      <>
        <Box
          component="button"
          type="button"
          aria-label={sponsor.name}
          aria-expanded={open}
          onClick={handleOpen}
          sx={{
            border: 'none',
            background: 'transparent',
            padding: 0,
            cursor: 'pointer',
            display: 'inline-flex',
          }}
        >
          {image}
        </Box>

        <Popover
          open={open}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          transformOrigin={{ vertical: 'top', horizontal: 'center' }}
          disableScrollLock
          marginThreshold={8}
          slotProps={{
            paper: {
              sx: {
                p: 2,
                maxWidth: 320,
              },
            },
          }}
        >
          <SponsorDetailContent sponsor={sponsor} onClose={handleClose} />
        </Popover>
      </>
    );
  }

  if (sponsor.url) {
    return (
      <RouteLink href={sponsor.url}>
        <a target="_blank" rel="noopener noreferrer">
          {image}
        </a>
      </RouteLink>
    );
  }

  return image;
};
